// Shared What's New tokens and helpers for the web app.
//
// Exact fills from the Figma What's New frames (banner 405:410, page 408:2,
// New chip 405:422, card badge 405:426) — same values as the desktop app so the
// two clients stay visually identical.
export const WHATS_NEW_COLORS = {
  bannerBg: '#111a31',
  bannerBorder: '#3153c8',
  bannerCtaBg: '#121a2a',
  bannerCtaBorder: '#4964db',
  rowBg: '#1b2233',
  rowBorder: '#242b3c',
  newPillBg: '#1f2e67',
  newPillBorder: '#3b57c9',
  newPillText: '#8fa5ff',
  updatePillBg: '#17351f',
  updatePillBorder: '#285b37',
  updatePillText: '#5fcb8a',
  cardBadgeBg: '#3153c8',
  newChipBg: '#172344',
  newChipBorder: '#3558c8',
  newChipCountBg: '#3153c8',
  unreadBadgeBg: '#243b8f',
  learnMoreBg: '#121a2a',
  learnMoreBorder: '#2a3556',
  sparkle: '#7e9bff'
};

// Accent tiles for "update" entries, which have no tool logo of their own.
export const WHATS_NEW_UPDATE_TILE_COLORS = ['#3152d7', '#249a55', '#e3a000', '#b07cf0', '#e5a93c'];

export const WHATS_NEW_FILTERS = [
  { label: 'All', value: 'all' },
  { label: 'New tools', value: 'new_tool' },
  { label: 'Updates', value: 'update' }
];

const MS_PER_DAY = 24 * 60 * 60 * 1000;

// Server sends SQLite UTC datetimes ('YYYY-MM-DD HH:MM:SS').
export function parseEntryDate(value) {
  if (!value) return null;
  const parsed = new Date(`${String(value).replace(' ', 'T')}Z`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

// "Added 3 days ago" / "Updated 2 weeks ago" — the verb tracks the entry type.
export function getRelativeLabel(entry) {
  const verb = entry.type === 'update' ? 'Updated' : 'Added';
  const published = parseEntryDate(entry.published_at);
  if (!published) return '';

  const days = Math.max(0, Math.floor((Date.now() - published.getTime()) / MS_PER_DAY));

  if (days === 0) return `${verb} today`;
  if (days === 1) return `${verb} yesterday`;
  if (days < 7) return `${verb} ${days} days ago`;
  if (days < 14) return `${verb} 1 week ago`;
  if (days < 28) return `${verb} ${Math.floor(days / 7)} weeks ago`;

  const months = Math.floor(days / 30);
  return months <= 1 ? `${verb} 1 month ago` : `${verb} ${months} months ago`;
}

// Entries are grouped by publication month. The current month keeps the
// design's "New this month" heading; older months are labelled by name so the
// page stays readable as history accumulates.
function getSectionLabel(published, now) {
  if (!published) return 'Earlier';

  if (published.getFullYear() === now.getFullYear() && published.getMonth() === now.getMonth()) {
    return 'New this month';
  }

  const month = published.toLocaleString(undefined, { month: 'long' });
  return published.getFullYear() === now.getFullYear() ? month : `${month} ${published.getFullYear()}`;
}

export function groupEntriesByMonth(entries) {
  const now = new Date();
  const sections = [];
  const byLabel = new Map();

  entries.forEach((entry) => {
    const label = getSectionLabel(parseEntryDate(entry.published_at), now);
    if (!byLabel.has(label)) {
      const section = { label, entries: [] };
      byLabel.set(label, section);
      sections.push(section);
    }
    byLabel.get(label).entries.push(entry);
  });

  return sections;
}

export function hashString(value) {
  let hash = 0;
  const text = String(value || '');
  for (let index = 0; index < text.length; index += 1) {
    hash = (hash << 5) - hash + text.charCodeAt(index);
    hash |= 0;
  }
  return Math.abs(hash);
}

function toolTitle(item) {
  return item?.title || item?.description || '';
}

// Links a What's New entry to a real tool.
//
// `linked_app_id` is OPTIONAL in the admin form, so relying on it alone means
// publishing "SEMrush" as a New Tool lights up nothing — no card NEW badge, no
// "New N" chip, no banner logo. Fall back to an exact (case-insensitive) title
// match, which is what an admin naturally types.
export function findWhatsNewApp(entry, apps) {
  const list = Array.isArray(apps) ? apps : [];

  if (entry?.linked_app_id) {
    const linked = String(entry.linked_app_id);
    const byId = list.find((app) => String(app?.id) === linked);
    if (byId) return byId;

    // The admin tool list is an array of tool-name strings, so a saved
    // linked_app_id can legitimately BE a title.
    const linkedTitle = linked.trim().toLowerCase();
    const byLinkedTitle = list.find((app) => toolTitle(app).trim().toLowerCase() === linkedTitle);
    if (byLinkedTitle) return byLinkedTitle;
  }

  const title = String(entry?.title || '').trim().toLowerCase();
  if (!title) return undefined;

  return list.find((app) => toolTitle(app).trim().toLowerCase() === title);
}

// App ids that should carry a NEW badge: every still-new "new tool" entry that
// resolves to a tool this user actually has.
export function collectNewAppIds(entries, apps) {
  const ids = new Set();

  (Array.isArray(entries) ? entries : []).forEach((entry) => {
    if (!entry || entry.type === 'update' || !entry.is_new) return;
    const app = findWhatsNewApp(entry, apps);
    if (app?.id !== undefined && app?.id !== null) {
      ids.add(String(app.id));
    }
  });

  return ids;
}
